<template>
  <li
    class="base-phone-row"
    :class="{ 'is-selected': selected, 'has-lead': hasLead(), 'has-actions': !!$slots.actions }"
  >
    <!-- Stays in place in both modes, so switching to selection doesn't rebuild the row. -->
    <div v-if="hasLead()" class="base-phone-row__lead">
      <FormCheckbox v-if="selectable" class="base-phone-row__check" :value="selected" @input="$emit('toggle-select')" />
      <slot v-else name="lead" />
    </div>

    <div class="base-phone-row__body">
      <!-- The title opens the row, and stretches over it (see __open::after): a tap anywhere
           opens it, and the row's own buttons sit above. -->
      <a v-if="openable && href" class="base-phone-row__open" :href="href" @click.prevent="onOpen">
        <slot name="title" />
      </a>
      <button
        v-else-if="openable"
        type="button"
        class="base-phone-row__open"
        :aria-haspopup="opensDialog ? 'dialog' : null"
        @click="onOpen"
      >
        <slot name="title" />
      </button>
      <div v-else class="base-phone-row__heading">
        <slot name="title" />
      </div>
      <slot />
    </div>

    <div v-if="$slots.actions" class="base-phone-row__actions">
      <slot name="actions" />
    </div>
  </li>
</template>

<script>
import FormCheckbox from '@/components/form/FormCheckbox.vue';

// A row of a phone list, the same in every list (tenders, opportunities, the tables…): an optional
// lead (an icon, or the checkbox while selecting), the title that opens the row, what follows it,
// and the row's actions in its corner. The `base-phone-row__*` classes lay out what follows:
// `__line` under the title, `__meta` for tags, `__facts` and `__block` for the tables.
export default {
  name: 'BasePhoneRow',
  components: {
    FormCheckbox,
  },
  props: {
    // The page the row opens, so it can also open in a new tab.
    href: {
      type: String,
      default: '',
    },
    openable: {
      type: Boolean,
      default: true,
    },
    // It opens a sheet or a dialog rather than a page.
    opensDialog: {
      type: Boolean,
      default: false,
    },
    selectable: {
      type: Boolean,
      default: false,
    },
    selected: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    // Slots aren't reactive, so not a computed: read at each render.
    hasLead() {
      return this.selectable || !!this.$slots.lead;
    },
    // While selecting, a tap selects instead.
    onOpen(event) {
      if (this.selectable) this.$emit('toggle-select');
      else this.$emit('open', event);
    },
  },
};
</script>

<style lang="scss">
.base-phone-row {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 12px 16px;
  background: $color-white;
  border-bottom: 1px solid $color-border;
  list-style: none;

  &.has-lead {
    padding-left: 12px;
  }

  // The actions' icons sit inside their 40px: 8px from the edge, they look the gutter's 16px away.
  // Without them (while selecting), the row keeps its 16px.
  &.has-actions {
    padding-right: 8px;
  }

  &.is-selected {
    background: $color-accent-100;
  }

  &.is-highlighted {
    background: $color-slate-100;
    transition: background-color 600ms ease;
  }

  // Above the stretched title, so the checkbox takes its own taps.
  &__lead {
    position: relative;
    z-index: 1;
    flex: none;
  }

  &__check {
    margin-top: 2px;
  }

  &__body {
    flex: 1;
    min-width: 0;
  }

  &__open {
    display: block;
    width: 100%;
    padding: 0;
    color: inherit;
    text-align: left;
    text-decoration: none;

    &::after {
      content: '';
      position: absolute;
      inset: 0;
    }

    &:focus-visible {
      outline: none;

      &::after {
        box-shadow: inset 0 0 0 2px $color-accent;
      }
    }
  }

  &__title {
    display: -webkit-box;
    overflow: hidden;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    font-size: 15px;
    font-weight: 500;
    line-height: 1.35;
    color: $color-normal;
  }

  // A line under the title (the emitter, the client…). Each line keeps the space above it, whichever
  // it follows: a tender without its emitter has its tags 6px under the title too.
  &__line {
    margin: 2px 0 0;
    overflow: hidden;
    font-size: 13px;
    color: $color-slate-500;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  // Its tags have their own size; a text beside them (a status's state, a profile's type) is a
  // line's, not the page's 16px.
  &__meta {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    margin-top: 6px;
    font-size: 13px;
  }

  // As the bottom sheets' facts: the label, then the value.
  &__facts {
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 4px 12px;
    margin: 8px 0 0;
    font-size: 13px;

    dt {
      color: $color-slate-500;
    }

    dd {
      min-width: 0;
      margin: 0;
      color: $color-text-body;
      overflow-wrap: anywhere;
    }
  }

  // Wide content across the row, under its name.
  &__block {
    margin-top: 8px;
    font-size: 13px;
  }

  &__block-label {
    margin: 0 0 4px;
    color: $color-slate-500;
  }

  // Above the stretched title, so they take their own taps. From the row's top edge to its bottom
  // one, its padding included. The first is centered on the title's line: in the row's 12px around
  // a 22px line, a one-line row's middle, whatever its size (a ⋯, a module's toggle, a contact's
  // score). The last sits in the row's last 40px, by its last line.
  &__actions {
    position: relative;
    z-index: 1;
    display: grid;
    flex: none;
    grid-template-rows: 46px;
    grid-auto-rows: 40px;
    align-content: space-between;
    align-items: center;
    align-self: stretch;
    gap: 0 4px;
    margin: -12px 0;
  }

  // A plain round button, not BaseRoundButton: a touch screen has no hover tooltip to show, and
  // each BaseRoundButton's tooltip popover is slow to tear down, for every row a list drops.
  &__action {
    position: relative;
    isolation: isolate;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    color: $color-slate-500;

    .base-icon {
      width: 18px;
      height: 18px;
    }

    // Its circle is 28px within the 40px it takes taps over: lit, it keeps clear of the row's edges.
    &::before {
      content: '';
      position: absolute;
      inset: 6px;
      z-index: -1;
      border-radius: $radius-full;
    }

    &.is-active {
      color: $color-accent;

      &::before {
        background: $color-accent-100;
      }
    }

    &:focus-visible {
      outline: none;

      &::before {
        box-shadow: 0 0 0 2px $color-accent;
      }
    }
  }
}
</style>
